import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const prefersReducedMotion = (): boolean =>
	typeof window !== "undefined" &&
	window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const isMobile = (): boolean =>
	typeof window !== "undefined" && window.innerWidth < 768;

function splitWords(el: HTMLElement): HTMLSpanElement[] {
	const text = el.textContent ?? "";
	const words = text.trim().split(/\s+/);
	el.innerHTML = "";
	const spans: HTMLSpanElement[] = [];
	words.forEach((word, i) => {
		const wrap = document.createElement("span");
		wrap.style.display = "inline-block";
		wrap.style.overflow = "hidden";
		wrap.style.verticalAlign = "top";
		const inner = document.createElement("span");
		inner.style.display = "inline-block";
		inner.style.willChange = "transform, opacity";
		inner.textContent = word;
		wrap.appendChild(inner);
		el.appendChild(wrap);
		if (i < words.length - 1) el.appendChild(document.createTextNode(" "));
		spans.push(inner);
	});
	return spans;
}

function clearWillChange(targets: gsap.TweenTarget): void {
	gsap.set(targets, { willChange: "auto" });
}

const triggers: ScrollTrigger[] = [];
const track = (t: ScrollTrigger | ScrollTrigger[] | undefined): void => {
	if (!t) return;
	if (Array.isArray(t)) triggers.push(...t);
	else triggers.push(t);
};

export function initHeroAnim(): void {
	const h1 = document.querySelector<HTMLElement>("#hero h1");
	const sub = document.querySelector<HTMLElement>("#hero [data-hero-sub]");
	const ctas = gsap.utils.toArray<HTMLElement>("#hero [data-hero-cta]");

	if (!h1) return;

	if (prefersReducedMotion()) {
		gsap.set([h1, sub, ...ctas], { opacity: 1, y: 0, scale: 1 });
		return;
	}

	const words = splitWords(h1);
	gsap.set(words, { yPercent: 110, opacity: 0 });
	if (sub) gsap.set(sub, { y: 16, opacity: 0 });
	gsap.set(ctas, { scale: 0.92, opacity: 0 });

	const tl = gsap.timeline({ defaults: { ease: "power3.out" } });
	tl.to(words, {
		yPercent: 0,
		opacity: 1,
		duration: 0.6,
		stagger: 0.06,
		onComplete: () => clearWillChange(words),
	});
	if (sub) {
		tl.to(
			sub,
			{ y: 0, opacity: 1, duration: 0.4, ease: "power2.out" },
			"-=0.2",
		);
	}
	tl.to(
		ctas,
		{
			scale: 1,
			opacity: 1,
			duration: 0.5,
			ease: "back.out(1.7)",
			stagger: 0.08,
		},
		"-=0.15",
	);
}

export function initProblemCounters(): void {
	const section = document.querySelector<HTMLElement>("#problem");
	if (!section) return;

	const title = section.querySelector<HTMLElement>("[data-problem-title]");
	const cards = gsap.utils.toArray<HTMLElement>("#problem [data-problem-card]");
	const counters = gsap.utils.toArray<HTMLElement>("#problem [data-counter]");

	if (prefersReducedMotion()) {
		gsap.set([title, ...cards].filter(Boolean) as HTMLElement[], {
			opacity: 1,
			y: 0,
		});
		counters.forEach((el) => {
			const target = Number(el.dataset.counter ?? "0");
			const suffix = el.dataset.counterSuffix ?? "";
			el.textContent = `${target}${suffix}`;
		});
		return;
	}

	if (title) gsap.set(title, { y: 24, opacity: 0 });
	gsap.set(cards, { y: 32, opacity: 0, willChange: "transform, opacity" });

	const tl = gsap.timeline({
		scrollTrigger: { trigger: section, start: "top 75%", once: true },
	});
	track(tl.scrollTrigger);

	if (title)
		tl.to(title, { y: 0, opacity: 1, duration: 0.5, ease: "power2.out" });
	tl.to(
		cards,
		{
			y: 0,
			opacity: 1,
			duration: 0.5,
			ease: "power3.out",
			stagger: 0.12,
			onComplete: () => clearWillChange(cards),
		},
		title ? "-=0.2" : 0,
	);

	counters.forEach((el) => {
		const target = Number(el.dataset.counter ?? "0");
		const suffix = el.dataset.counterSuffix ?? "";
		const obj = { val: 0 };
		el.textContent = `0${suffix}`;
		const st = ScrollTrigger.create({
			trigger: el,
			start: "top 80%",
			once: true,
			onEnter: () => {
				gsap.to(obj, {
					val: target,
					duration: 1.2,
					ease: "power2.out",
					snap: { val: 1 },
					onUpdate: () => {
						el.textContent = `${Math.round(obj.val)}${suffix}`;
					},
				});
			},
		});
		track(st);
	});
}

export function initSolutionCards(): void {
	const section = document.querySelector<HTMLElement>("#solution");
	if (!section) return;

	const cards = gsap.utils.toArray<HTMLElement>(
		"#solution [data-solution-card]",
	);
	if (!cards.length) return;

	if (prefersReducedMotion()) {
		gsap.set(cards, { opacity: 1, scale: 1 });
		return;
	}

	gsap.set(cards, {
		scale: 0.94,
		opacity: 0,
		willChange: "transform, opacity",
	});

	const st = ScrollTrigger.create({
		trigger: section,
		start: "top 70%",
		once: true,
		onEnter: () => {
			gsap.to(cards, {
				scale: 1,
				opacity: 1,
				duration: 0.5,
				ease: "expo.out",
				stagger: { each: 0.1, from: "center" },
				onComplete: () => clearWillChange(cards),
			});
		},
	});
	track(st);

	cards.forEach((card) => {
		const icon = card.querySelector<HTMLElement>("[data-solution-icon]");
		if (!icon) return;
		card.addEventListener("mouseenter", () =>
			gsap.to(icon, {
				rotate: 8,
				scale: 1.08,
				duration: 0.2,
				ease: "back.out(2)",
			}),
		);
		card.addEventListener("mouseleave", () =>
			gsap.to(icon, {
				rotate: 0,
				scale: 1,
				duration: 0.25,
				ease: "power2.out",
			}),
		);
	});
}

export function initHowItWorksTimeline(): void {
	const section = document.querySelector<HTMLElement>("#how-it-works");
	if (!section) return;

	const stage = section.querySelector<HTMLElement>("[data-hiw-stage]");
	const steps = gsap.utils.toArray<HTMLElement>(
		"#how-it-works [data-hiw-step]",
	);
	if (!stage || !steps.length) return;

	if (isMobile() || prefersReducedMotion()) {
		if (prefersReducedMotion()) {
			gsap.set(steps, { opacity: 1, scale: 1 });
			return;
		}
		gsap.set(steps, { y: 24, opacity: 0 });
		const st = ScrollTrigger.create({
			trigger: section,
			start: "top 70%",
			once: true,
			onEnter: () =>
				gsap.to(steps, {
					y: 0,
					opacity: 1,
					duration: 0.5,
					ease: "power3.out",
					stagger: 0.12,
				}),
		});
		track(st);
		return;
	}

	const maxScroll = stage.scrollWidth - window.innerWidth;
	if (maxScroll <= 0) return;

	gsap.set(steps, { opacity: 0.3, scale: 0.9 });
	gsap.set(steps[0], { opacity: 1, scale: 1 });

	const tween = gsap.to(stage, { x: -maxScroll, ease: "none" });

	const pinTrigger = ScrollTrigger.create({
		trigger: section,
		start: "top top",
		end: () => `+=${maxScroll + 200}`,
		pin: true,
		scrub: 1,
		anticipatePin: 1,
		animation: tween,
		invalidateOnRefresh: true,
	});
	track(pinTrigger);

	steps.forEach((step, i) => {
		const segment = 1 / steps.length;
		const enterAt = segment * i;
		const exitAt = segment * (i + 1);
		const st = ScrollTrigger.create({
			trigger: section,
			start: () => `top+=${(maxScroll + 200) * enterAt} top`,
			end: () => `top+=${(maxScroll + 200) * exitAt} top`,
			onEnter: () =>
				gsap.to(step, {
					opacity: 1,
					scale: 1,
					duration: 0.4,
					ease: "power2.out",
				}),
			onEnterBack: () =>
				gsap.to(step, {
					opacity: 1,
					scale: 1,
					duration: 0.4,
					ease: "power2.out",
				}),
			onLeave: () =>
				gsap.to(step, {
					opacity: 0.3,
					scale: 0.9,
					duration: 0.4,
					ease: "power2.out",
				}),
			onLeaveBack: () =>
				gsap.to(step, {
					opacity: 0.3,
					scale: 0.9,
					duration: 0.4,
					ease: "power2.out",
				}),
		});
		track(st);
	});
}

type CalcChangeHandler = (newValue: number) => void;

export function initCalculator(onChange?: CalcChangeHandler): void {
	const output = document.querySelector<HTMLElement>(
		"#calculator [data-calc-output]",
	);
	if (!output) return;

	const formatMoney = (n: number): string =>
		new Intl.NumberFormat("ru-RU").format(Math.round(n)) + " ₽";

	const state = { val: Number(output.dataset.calcInitial ?? "0") };
	output.textContent = formatMoney(state.val);

	const updateTo = (target: number): void => {
		if (prefersReducedMotion()) {
			state.val = target;
			output.textContent = formatMoney(target);
			onChange?.(target);
			return;
		}
		gsap.to(state, {
			val: target,
			duration: 0.4,
			ease: "power2.out",
			snap: { val: 1 },
			onUpdate: () => {
				output.textContent = formatMoney(state.val);
			},
			onComplete: () => onChange?.(target),
		});
		gsap.fromTo(
			output,
			{ scale: 1 },
			{
				scale: 1.04,
				duration: 0.12,
				ease: "power2.out",
				yoyo: true,
				repeat: 1,
			},
		);
	};

	(window as unknown as { __calcUpdate?: (n: number) => void }).__calcUpdate =
		updateTo;
}

export function initPricingReveal(): void {
	const section = document.querySelector<HTMLElement>("#pricing");
	if (!section) return;

	const cards = gsap.utils.toArray<HTMLElement>("#pricing [data-pricing-card]");
	const featured = section.querySelector<HTMLElement>(
		"[data-pricing-card='medium']",
	);
	const sideCards = cards.filter((c) => c !== featured);

	if (prefersReducedMotion()) {
		gsap.set(cards, { opacity: 1, scale: 1 });
		if (featured) gsap.set(featured, { scale: 1.05 });
		return;
	}

	gsap.set(sideCards, {
		scale: 0.94,
		opacity: 0,
		willChange: "transform, opacity",
	});
	if (featured)
		gsap.set(featured, {
			scale: 0.98,
			opacity: 0,
			willChange: "transform, opacity",
		});

	const tl = gsap.timeline({
		scrollTrigger: { trigger: section, start: "top 70%", once: true },
	});
	track(tl.scrollTrigger);

	tl.to(sideCards, {
		scale: 1,
		opacity: 1,
		duration: 0.5,
		ease: "power3.out",
		stagger: { each: 0.1, from: "edges" },
		onComplete: () => clearWillChange(sideCards),
	});

	if (featured) {
		tl.to(
			featured,
			{
				scale: 1.05,
				opacity: 1,
				duration: 0.7,
				ease: "expo.out",
				onComplete: () => clearWillChange(featured),
			},
			"-=0.35",
		);
	}
}

export function initTrustReveal(): void {
	const section = document.querySelector<HTMLElement>("#trust");
	if (!section) return;

	const title = section.querySelector<HTMLElement>("[data-trust-title]");
	const items = gsap.utils.toArray<HTMLElement>("#trust [data-trust-item]");
	const targets = [title, ...items].filter(Boolean) as HTMLElement[];
	if (!targets.length) return;

	if (prefersReducedMotion()) {
		gsap.set(targets, { opacity: 1, y: 0 });
		return;
	}

	gsap.set(targets, { y: 16, opacity: 0 });
	const tl = gsap.timeline({
		scrollTrigger: { trigger: section, start: "top 75%", once: true },
	});
	track(tl.scrollTrigger);
	tl.to(targets, {
		y: 0,
		opacity: 1,
		duration: 0.4,
		ease: "power2.out",
		stagger: 0.08,
	});
}

export function initCtaForm(): void {
	const section = document.querySelector<HTMLElement>("#cta-form");
	if (!section) return;

	const inputs = section.querySelectorAll<HTMLElement>("input, textarea");
	const submit = section.querySelector<HTMLElement>("[data-cta-submit]");

	if (!prefersReducedMotion()) {
		gsap.set(section, { opacity: 0, y: 24 });
		const revealSt = ScrollTrigger.create({
			trigger: section,
			start: "top 75%",
			once: true,
			onEnter: () =>
				gsap.to(section, {
					opacity: 1,
					y: 0,
					duration: 0.5,
					ease: "power2.out",
				}),
		});
		track(revealSt);
	} else {
		gsap.set(section, { opacity: 1, y: 0 });
	}

	inputs.forEach((input) => {
		input.addEventListener("focus", () => {
			if (prefersReducedMotion()) return;
			gsap.to(input, {
				boxShadow: "0 0 0 3px rgba(197,255,74,0.35)",
				duration: 0.2,
				ease: "power2.out",
			});
		});
		input.addEventListener("blur", () => {
			gsap.to(input, {
				boxShadow: "0 0 0 0px rgba(197,255,74,0)",
				duration: 0.2,
				ease: "power2.out",
			});
		});
	});

	if (submit && !isMobile() && !prefersReducedMotion()) {
		const strength = 8;
		const bounds = () => submit.getBoundingClientRect();
		const move = (e: MouseEvent) => {
			const b = bounds();
			const cx = b.left + b.width / 2;
			const cy = b.top + b.height / 2;
			const dx = (e.clientX - cx) / (b.width / 2);
			const dy = (e.clientY - cy) / (b.height / 2);
			gsap.to(submit, {
				x: dx * strength,
				y: dy * strength,
				duration: 0.3,
				ease: "power3.out",
			});
		};
		const reset = () =>
			gsap.to(submit, { x: 0, y: 0, duration: 0.4, ease: "power3.out" });
		submit.addEventListener("mousemove", move);
		submit.addEventListener("mouseleave", reset);
	}
}

const SAFETY_SELECTOR = [
	"#problem [data-problem-title]",
	"#problem [data-problem-card]",
	"#solution [data-solution-card]",
	"#how-it-works [data-hiw-step]",
	"#pricing [data-pricing-card]",
	"#trust [data-trust-title]",
	"#trust [data-trust-item]",
	"#cta-form",
].join(", ");

function revealStuck(): void {
	const stuck = document.querySelectorAll<HTMLElement>(SAFETY_SELECTOR);
	stuck.forEach((el) => {
		const op = parseFloat(getComputedStyle(el).opacity);
		if (op < 0.05) {
			gsap.set(el, { clearProps: "opacity,y,scale,transform,willChange" });
			el.style.opacity = "1";
		}
	});
}

export function initMotion(): void {
	if (document.fonts && document.fonts.ready) {
		document.fonts.ready.then(() => ScrollTrigger.refresh());
	}

	initHeroAnim();
	initProblemCounters();
	initSolutionCards();
	initHowItWorksTimeline();
	initCalculator();
	initPricingReveal();
	initTrustReveal();
	initCtaForm();

	// Safety-net: если ScrollTrigger по любой причине не выстрелил
	// (Playwright full-page capture, медленный layout, JS-ошибка в одной из секций),
	// принудительно показываем элементы через 4 секунды.
	setTimeout(() => {
		ScrollTrigger.refresh();
		revealStuck();
	}, 4000);
}

export function destroyAll(): void {
	triggers.forEach((t) => t.kill());
	triggers.length = 0;
	ScrollTrigger.getAll().forEach((t) => t.kill());
	gsap.globalTimeline.clear();
}
